import { Component, EventEmitter, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import {
    forLifeOf,
    getDynamicRouterLink,
    getRouterLinkById,
    LifecycleAware
} from '@lsq-frontend/common-ui';
import { Icons } from '@lsq-frontend/common-ui';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import {
    GetBuyerImplementationConfigurationResponse,
    GetBuyerImplementationConfigurationService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-buyer-implementation/get-buyer-implementation-configuration';
import {
    ADMIN_MANAGE_BUYER_CONFIGURATION,
    atlas
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import {
    formatBpsInput,
    formatHbpsInput,
    handleFboAccountType
} from '../configuration/buyer-configuration-helpers';
import { ManageBuyerService } from '../manage-buyer.service';

@Component({
    selector: 'review-buyer-configuration',
    styleUrls: ['./review-buyer-section.scss'],
    encapsulation: ViewEncapsulation.None,
    template: `
        <buyer-review-section
            [sectionTitle]="'Buyer Configuration'"
            [editLocation]="editLocationUrl$ | async"
        >
            <div *ngIf="!cachedResponse">
                <lsq-circle-loader></lsq-circle-loader>
            </div>
            <div *ngIf="cachedResponse" class="review-buyer-section">
                <info-box *ngIf="isMissingData()">
                    You have missing information that needs your attention.
                    <span
                        *ngIf="
                            cachedResponse.buyerDefaultFlatFeeRatePercent === null &&
                            cachedResponse.buyerDecayingFeeRatePercent === null
                        "
                    >
                        (Must either have a Default Flat Rate Fee or Decaying Fee Rate set)
                    </span>
                </info-box>
                <review-buyer-configuration-section sectionTitle="Credit Limit">
                    <div class="row">
                        <div class="col-12 col-lg-6">
                            <p class="label">
                                <span>Credit Limit</span>
                                <lsq-icon
                                    id="buyerCreditLimitCentsWarning"
                                    *ngIf="
                                        cachedResponse.buyerCreditLimitCents === null &&
                                        cachedResponse.accountCreditLimitCents === null
                                    "
                                    [type]="icons.FaExclamationTriangleSolid"
                                ></lsq-icon>
                            </p>
                            <p class="body text-dark-gray" id="buyerCreditLimitCents">
                                {{ cachedResponse.buyerCreditLimitCents / 100 | currencyDash }}
                            </p>
                        </div>
                        <div class="col-12 col-lg-6">
                            <p class="label">
                                <span>Credit Limit Currency</span>
                                <lsq-icon
                                    id="buyerCreditLimitCurrencyCodeWarning"
                                    *ngIf="
                                        cachedResponse.buyerCreditLimitCurrencyCode === null &&
                                        cachedResponse.accountCreditLimitCurrencyCode === null
                                    "
                                    [type]="icons.FaExclamationTriangleSolid"
                                ></lsq-icon>
                            </p>
                            <p class="body text-dark-gray" id="buyerCreditLimitCurrencyCode">
                                {{
                                    cachedResponse.buyerCreditLimitCurrencyCode === null
                                        ? '—'
                                        : cachedResponse.buyerCreditLimitCurrencyCode
                                }}
                            </p>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12 col-lg-6">
                            <p class="label">
                                <span>Default Flat Fee Rate</span>
                                <lsq-icon
                                    id="buyerDefaultFlatFeeRatePercentWarning"
                                    *ngIf="
                                        cachedResponse.buyerDefaultFlatFeeRatePercent === null &&
                                        cachedResponse.buyerDecayingFeeRatePercent === null
                                    "
                                    [type]="icons.FaExclamationTriangleSolid"
                                ></lsq-icon>
                            </p>
                            <p class="body text-dark-gray" id="buyerDefaultFlatFeeRatePercent">
                                {{
                                    cachedResponse.buyerDefaultFlatFeeRatePercent === null
                                        ? '—'
                                        : formatBps(cachedResponse.buyerDefaultFlatFeeRatePercent) +
                                          '%'
                                }}
                            </p>
                        </div>
                        <div class="col-12 col-lg-6">
                            <p class="label">
                                <span>Decaying Fee Rate</span>
                                <lsq-icon
                                    id="buyerDecayingFeeRatePercentWarning"
                                    *ngIf="
                                        cachedResponse.buyerDefaultFlatFeeRatePercent === null &&
                                        cachedResponse.buyerDecayingFeeRatePercent === null
                                    "
                                    [type]="icons.FaExclamationTriangleSolid"
                                ></lsq-icon>
                            </p>
                            <p class="body text-dark-gray" id="buyerDecayingFeeRatePercent">
                                {{
                                    cachedResponse.buyerDecayingFeeRatePercent === null
                                        ? '—'
                                        : formatHbps(cachedResponse.buyerDecayingFeeRatePercent) +
                                          '%'
                                }}
                            </p>
                        </div>
                    </div>
                </review-buyer-configuration-section>
                <review-buyer-configuration-section sectionTitle="Buyer Grace Period">
                    <div class="row">
                        <div class="col-12 col-lg-6">
                            <p class="label">Buyer Grace Period</p>
                            <p class="body text-dark-gray" id="buyerGracePeriodDays">
                                {{
                                    cachedResponse.buyerGracePeriodDays === null
                                        ? '—'
                                        : cachedResponse.buyerGracePeriodDays + ' Days'
                                }}
                            </p>
                        </div>
                    </div>
                </review-buyer-configuration-section>
                <review-buyer-configuration-section sectionTitle="Past Due Fee">
                    <div class="row">
                        <div class="col-12 col-lg-6">
                            <p class="label">Past Due Fee</p>
                            <p class="body text-dark-gray" id="buyerPastDueFeePercent">
                                {{
                                    cachedResponse.buyerPastDueFeePercent === null
                                        ? '—'
                                        : formatBps(cachedResponse.buyerPastDueFeePercent) + '%'
                                }}
                            </p>
                        </div>
                    </div>
                </review-buyer-configuration-section>
                <review-buyer-configuration-section sectionTitle="Buyer Paid Fee">
                    <div class="row">
                        <div class="col-12 col-lg-6">
                            <p class="label">Buyer Paid Fee</p>
                            <p class="body text-dark-gray" id="buyerPaidFeePercentage">
                                {{
                                    cachedResponse.buyerPaidFeePercentage === null
                                        ? '—'
                                        : cachedResponse.buyerPaidFeePercentage + '%'
                                }}
                            </p>
                        </div>
                    </div>
                </review-buyer-configuration-section>
                <review-buyer-configuration-section sectionTitle="Ineligible Days Before Terms">
                    <div class="row">
                        <div class="col-12">
                            <p class="label">Ineligible Days Before Terms</p>
                            <p class="body text-dark-gray" id="buyerIneligibleDaysBeforeTerms">
                                {{
                                    cachedResponse.buyerIneligibleDaysBeforeTerms === null
                                        ? '—'
                                        : cachedResponse.buyerIneligibleDaysBeforeTerms
                                }}
                            </p>
                        </div>
                    </div>
                </review-buyer-configuration-section>
                <review-buyer-configuration-section sectionTitle="Revenue Share Configuration">
                    <div class="row">
                        <div class="col-12 col-lg-6">
                            <p class="label">Platform Revenue Share</p>
                            <p class="body text-dark-gray" id="buyerPlatformRevenueSharePercent">
                                {{
                                    cachedResponse.buyerPlatformRevenueSharePercent === null
                                        ? '—'
                                        : formatBps(
                                              cachedResponse.buyerPlatformRevenueSharePercent
                                          ) + '%'
                                }}
                            </p>
                        </div>
                        <div class="col-12 col-lg-6">
                            <p class="label">LSQ Revenue Share</p>
                            <p class="body text-dark-gray" id="buyerLsqRevenueSharePercent">
                                {{
                                    cachedResponse.buyerLsqRevenueSharePercent === null
                                        ? '—'
                                        : formatBps(cachedResponse.buyerLsqRevenueSharePercent) +
                                          '%'
                                }}
                            </p>
                        </div>
                        <div class="col-12 col-lg-6">
                            <p class="label">Primary Buyer Revenue Share</p>
                            <p
                                class="body text-dark-gray"
                                id="buyerPrimaryBuyerRevenueSharePercent"
                            >
                                {{
                                    cachedResponse.buyerPrimaryBuyerRevenueSharePercent === null
                                        ? '—'
                                        : formatBps(
                                              cachedResponse.buyerPrimaryBuyerRevenueSharePercent
                                          ) + '%'
                                }}
                            </p>
                        </div>
                        <div class="col-12 col-lg-6">
                            <p class="label">Secondary Buyer Revenue Share</p>
                            <p
                                class="body text-dark-gray"
                                id="buyerSecondaryBuyerRevenueSharePercent"
                            >
                                {{
                                    cachedResponse.buyerSecondaryBuyerRevenueSharePercent === null
                                        ? '—'
                                        : formatBps(
                                              cachedResponse.buyerSecondaryBuyerRevenueSharePercent
                                          ) + '%'
                                }}
                            </p>
                        </div>
                        <div class="col-12 col-lg-6">
                            <p class="label">Primary Due Date Threshold</p>
                            <p class="body text-dark-gray" id="buyerPrimaryDueDateThreshold">
                                {{
                                    cachedResponse.buyerPrimaryDueDateThreshold === null
                                        ? '—'
                                        : cachedResponse.buyerPrimaryDueDateThreshold
                                }}
                            </p>
                        </div>
                        <div class="col-12 col-lg-6">
                            <p class="label">Secondary Due Date Threshold</p>
                            <p class="body text-dark-gray" id="buyerSecondaryDueDateThreshold">
                                {{
                                    cachedResponse.buyerSecondaryDueDateThreshold === null
                                        ? '—'
                                        : cachedResponse.buyerSecondaryDueDateThreshold
                                }}
                            </p>
                        </div>
                    </div>
                    <div class="divider"></div>
                    <div class="row">
                        <div class="col-12 col-lg-6">
                            <p class="label">
                                <span>Account Name</span>
                                <lsq-icon
                                    id="bankAccountNameWarning"
                                    *ngIf="
                                        hasRevenueShareConfigured() &&
                                        !cachedResponse.bankAccountName
                                    "
                                    [type]="icons.FaExclamationTriangleSolid"
                                ></lsq-icon>
                            </p>
                            <p class="body text-dark-gray" id="bankAccountName">
                                {{
                                    cachedResponse.bankAccountName === null
                                        ? '—'
                                        : cachedResponse.bankAccountName
                                }}
                            </p>
                        </div>
                        <div class="col-12 col-lg-6">
                            <p class="label">
                                <span>Bank Name</span>
                                <lsq-icon
                                    id="bankAccountBankNameWarning"
                                    *ngIf="
                                        hasRevenueShareConfigured() &&
                                        !cachedResponse.bankAccountBankName
                                    "
                                    [type]="icons.FaExclamationTriangleSolid"
                                ></lsq-icon>
                            </p>
                            <p class="body text-dark-gray" id="bankAccountBankName">
                                {{
                                    cachedResponse.bankAccountBankName === null
                                        ? '—'
                                        : cachedResponse.bankAccountBankName
                                }}
                            </p>
                        </div>
                        <div class="col-12 col-lg-6">
                            <p class="label">
                                <span>Account Number</span>
                                <lsq-icon
                                    id="bankAccountNumberLastFourWarning"
                                    *ngIf="
                                        hasRevenueShareConfigured() &&
                                        !cachedResponse.bankAccountNumberLastFour
                                    "
                                    [type]="icons.FaExclamationTriangleSolid"
                                ></lsq-icon>
                            </p>
                            <p class="body text-dark-gray" id="bankAccountNumberLastFour">
                                {{
                                    cachedResponse.bankAccountNumberLastFour === null
                                        ? '—'
                                        : '*****' + cachedResponse.bankAccountNumberLastFour
                                }}
                            </p>
                        </div>
                        <div class="col-12 col-lg-6">
                            <p class="label">
                                <span>Routing Number</span>
                                <lsq-icon
                                    id="bankAccountRoutingNumberLastFourWarning"
                                    *ngIf="
                                        hasRevenueShareConfigured() &&
                                        !cachedResponse.bankAccountRoutingNumberLastFour
                                    "
                                    [type]="icons.FaExclamationTriangleSolid"
                                ></lsq-icon>
                            </p>
                            <p class="body text-dark-gray" id="bankAccountRoutingNumberLastFour">
                                {{
                                    cachedResponse.bankAccountRoutingNumberLastFour === null
                                        ? '—'
                                        : '*****' + cachedResponse.bankAccountRoutingNumberLastFour
                                }}
                            </p>
                        </div>
                    </div>
                    <br />
                </review-buyer-configuration-section>
                <review-buyer-configuration-section sectionTitle="Total Payment Management">
                    <div class="row">
                        <div class="col-12 col-lg-6">
                            <p class="label">
                                Account Name
                            </p>
                            <p class="body text-dark-gray" id="tpmAccountName">
                                {{
                                    cachedResponse.fboConfiguration?.accountName !== null
                                        ? cachedResponse.fboConfiguration?.accountName
                                        : '—'
                                }}
                            </p>
                        </div>
                        <div class="col-12 col-lg-6">
                            <p class="label">
                                Bank Name
                            </p>
                            <p class="body text-dark-gray" id="tpmBankName">
                                {{
                                    cachedResponse.fboConfiguration?.bankName !== null
                                        ? cachedResponse.fboConfiguration?.bankName
                                        : '—'
                                }}
                            </p>
                        </div>
                        <div class="col-12 col-lg-6">
                            <p class="label">
                                Account Number
                            </p>
                            <p class="body text-dark-gray" id="tpmAccountNumberLastFour">
                                {{
                                    cachedResponse.fboConfiguration?.accountNumberLastFour !== null
                                        ? '*****' +
                                          cachedResponse.fboConfiguration?.accountNumberLastFour
                                        : '—'
                                }}
                            </p>
                        </div>
                        <div class="col-12 col-lg-6">
                            <p class="label">
                                ACH Routing Number
                            </p>
                            <p class="body text-dark-gray" id="tpmAchRoutingNumberLastFour">
                                {{
                                    cachedResponse.fboConfiguration?.achRoutingNumberLastFour !==
                                    null
                                        ? '*****' +
                                          cachedResponse.fboConfiguration?.achRoutingNumberLastFour
                                        : '—'
                                }}
                            </p>
                        </div>
                        <div class="col-12 col-lg-6">
                            <p class="label">
                                Wire Routing Number
                            </p>
                            <p class="body text-dark-gray" id="tpmWireRoutingNumberLastFour">
                                {{
                                    cachedResponse.fboConfiguration?.wireRoutingNumberLastFour !==
                                    null
                                        ? '*****' +
                                          cachedResponse.fboConfiguration?.wireRoutingNumberLastFour
                                        : '—'
                                }}
                            </p>
                        </div>
                    </div>
                    <div class="divider"></div>
                    <div class="row">
                        <div class="col-12 col-lg-6">
                            <p class="label">ACH Company ID</p>
                            <p class="body text-dark-gray" id="tpmCompanyId">
                                {{
                                    !!cachedResponse.fboConfiguration?.bankCompanyId
                                        ? cachedResponse.fboConfiguration?.bankCompanyId
                                        : '—'
                                }}
                            </p>
                        </div>
                        <div class="col-12 col-lg-6">
                            <p class="label">ACH Sender ID</p>
                            <p class="body text-dark-gray" id="tpmSenderId">
                                {{
                                    !!cachedResponse.fboConfiguration?.bankSenderId
                                        ? cachedResponse.fboConfiguration?.bankSenderId
                                        : '—'
                                }}
                            </p>
                        </div>
                        <div class="col-12 col-lg-6">
                            <p class="label">FBO Account Type</p>
                            <p class="body text-dark-gray" id="tpmFboAccountType">
                                {{
                                    !!cachedResponse.fboConfiguration?.fboAccountType
                                        ? formatFboAccountType(
                                              cachedResponse.fboConfiguration?.fboAccountType
                                          )
                                        : '—'
                                }}
                            </p>
                        </div>
                        <div class="col-12 col-lg-6">
                            <p class="label">Payment Instruction Output Folder Id</p>
                            <p class="body text-dark-gray" id="tpmOutputFolderId">
                                {{
                                    !!cachedResponse.fboConfiguration?.outputFolderId
                                        ? cachedResponse.fboConfiguration?.outputFolderId
                                        : '—'
                                }}
                            </p>
                        </div>
                    </div>
                </review-buyer-configuration-section>
            </div>
        </buyer-review-section>
    `
})
@LifecycleAware()
export class ReviewBuyerConfigurationComponent implements OnInit {
    @Output() public completed = new EventEmitter<boolean>();

    private _fetchSubject = new Subject();

    public cachedResponse: GetBuyerImplementationConfigurationResponse;

    public editLocationUrl$: Observable<UrlTree>;
    public formatBps = formatBpsInput;
    public formatHbps = formatHbpsInput;
    public formatFboAccountType = handleFboAccountType;

    public icons = Icons;
    constructor(
        private _getBuyerImplementationConfigurationService: GetBuyerImplementationConfigurationService,
        private _manageBuyerService: ManageBuyerService,
        private _router: Router
    ) {}

    public ngOnInit(): void {
        combineLatest([
            this._manageBuyerService.buyerKey$,
            this._fetchSubject.pipe(startWith(true))
        ])
            .pipe(
                forLifeOf(this),
                filter((buyerKey) => !!buyerKey)
            )
            .subscribe(async ([buyerKey, _]) => {
                if (buyerKey) {
                    this.cachedResponse = await this._getBuyerImplementationConfigurationService.executeOnce(
                        {
                            buyerImplementationKey: buyerKey
                        }
                    );
                }

                this.completed.emit(!this.isMissingData());
            });

        this.editLocationUrl$ = this._manageBuyerService.buyerKey$.pipe(
            map((buyerKey) => {
                const urlTree = this._router.createUrlTree(
                    getDynamicRouterLink(
                        getRouterLinkById(atlas, ADMIN_MANAGE_BUYER_CONFIGURATION, {
                            noStandaloneLeadingSlash: true
                        }),
                        { buyerKey }
                    ),
                    {
                        queryParams: {
                            isImplementation: true
                        }
                    }
                );
                return urlTree;
            })
        );
    }

    public isMissingData(): boolean {
        return (
            (this.hasRevenueShareConfigured() && !this.cachedResponse.bankAccountName) ||
            (this.cachedResponse.buyerCreditLimitCents === null &&
                this.cachedResponse.accountCreditLimitCents === null) ||
            (this.cachedResponse.buyerDecayingFeeRatePercent === null &&
                this.cachedResponse.buyerDefaultFlatFeeRatePercent === null)
        );
    }

    public hasRevenueShareConfigured(): boolean {
        return (
            this.cachedResponse.buyerPlatformRevenueSharePercent !== null ||
            this.cachedResponse.buyerLsqRevenueSharePercent !== null ||
            this.cachedResponse.buyerPrimaryBuyerRevenueSharePercent !== null ||
            this.cachedResponse.buyerSecondaryBuyerRevenueSharePercent !== null ||
            this.cachedResponse.buyerPrimaryDueDateThreshold !== null ||
            this.cachedResponse.buyerSecondaryDueDateThreshold !== null
        );
    }
}
